import { FC, useState } from "react";
import toast from "react-hot-toast";
import { getErrorFromCatch } from "../../../api/api";
import { orderApi, TResendKind } from "../../../api/order.api";
import { IOrder } from "../../../model/IOrder";
import Modal from "../../common/Modal";
import Button from "../../form/Button";

interface IResendAction {
  kind: TResendKind;
  label: string;
  /** Who it goes to, for the confirmation. */
  recipient: (order: IOrder) => string;
  /** Whether this order qualifies at all. */
  applies: (order: IOrder) => boolean;
}

// --
// -- A fee at or below this is not protection anyone bought; the backend uses
// -- the same floor (shipping-protection.min-fee) before it sends.
// --
const MIN_PROTECTION_FEE = 0.1;

const ACTIONS: IResendAction[] = [
  {
    kind: 'partscare-offer',
    label: 'Resend PartsCare Offer',
    recipient: (order) => order.siEmail || 'the customer',
    // -- Only where a part can still be covered. An expired offer or one
    // -- already taken up has nothing to offer, which is the same answer the
    // -- backend's pricing quote gives.
    applies: (order) => (order.orderItems || []).some((item) =>
      !!item.partscare?.eligible && !item.partscare?.subscribed && !item.partscare?.offerExpired),
  },
  {
    kind: 'shipping-protection',
    label: 'Resend Shipping Protection Email',
    recipient: (order) => order.siEmail || 'the customer',
    applies: (order) => !!order.shippingProtection
      && (order.shippingProtectionFee ?? 0) > MIN_PROTECTION_FEE,
  },
  {
    kind: 'vendor',
    label: 'Resend Email to Vendor',
    recipient: (order) => {
      const names = Array.from(new Set((order.orderItems || [])
        .map((item) => item.vendor?.name || item.supplierExternalKey)
        .filter(Boolean)));
      return names.length ? names.join(', ') : 'the order\'s vendors';
    },
    applies: (order) => (order.orderItems || []).some((item) => !!item.supplierExternalKey),
  },
];

/**
 * Resending an order's emails, from the order page.
 *
 * Each one asks first. These reach real customers and vendors, and the button
 * sits next to ordinary read-only information where a misclick is easy - a
 * second email about an order someone already has is a support call.
 *
 * Buttons appear only for the emails this order qualifies for, so the page
 * does not offer a PartsCare resend on an order with nothing eligible.
 */
const ResendEmailButtons: FC<{ order: IOrder }> = ({ order }) => {
  const [confirming, setConfirming] = useState<IResendAction | null>(null);
  const [resend, { isLoading }] = orderApi.useResendOrderEmailMutation();

  const available = ACTIONS.filter((action) => action.applies(order));
  if (!available.length) return null;

  const send = async () => {
    if (!confirming) return;
    try {
      const result = await resend({ orderId: order.orderId, kind: confirming.kind }).unwrap();

      // -- sent: false is a reason, not a failure - the order did not qualify
      // -- after all. Saying "failed" would have someone try again forever.
      if (result.sent) toast.success(`${confirming.label}: ${result.message}`, { duration: 6000 });
      else toast(`Not sent. ${result.message}`, { duration: 8000, icon: 'ℹ️' });

      setConfirming(null);
    } catch (err) {
      toast.error(getErrorFromCatch(err) || 'Could not resend the email');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {available.map((action) => (
        <Button
          key={action.kind}
          isNotFullWidth
          size="small"
          variant="outline"
          className="whitespace-nowrap"
          disabled={isLoading}
          onClick={() => setConfirming(action)}
        >
          {action.label}
        </Button>
      ))}

      {!!confirming && (
        <Modal
          isShown
          title={confirming.label}
          style={{ maxWidth: '520px' }}
          onClose={() => setConfirming(null)}
        >
          <div className="flex flex-col gap-5">
            <div>
              Send this email again for order <b>{order.orderId}</b>?
              <div className="text-text_pale mt-1">
                It will go to <b>{confirming.recipient(order)}</b>.
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button isNotFullWidth disabled={isLoading} onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button isNotFullWidth type="submit" disabled={isLoading} onClick={send}>
                {isLoading ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ResendEmailButtons;
